import { useState } from 'react';
import { RiFolderFill, RiFileLine, RiUpload2Line, RiLoader4Line, RiArchive2Fill } from 'react-icons/ri';
import { MdStorage, MdDeleteOutline, MdWorkspaces, MdBackup } from 'react-icons/md';
import { formatSize } from '../utils/paths';

const PANEL_FILES = {
  'PanelStorage':  { label: 'Panel Storage',     Icon: MdStorage,       color: 'text-purple-400' },
  'recycle_bin':   { label: 'Recycle Bin',        Icon: MdDeleteOutline, color: 'text-purple-400' },
  'workspaces':    { label: 'Workspaces',         Icon: MdWorkspaces,    color: 'text-purple-400' },
  '.pteroignore':  { label: 'Backup Exclusion',   Icon: MdBackup,        color: 'text-purple-400' },
};

const isPanelFile = (name) => PANEL_FILES.hasOwnProperty(name);

const SelectAllCheckbox = ({ entries, selectedSet, showPanelFiles, onToggleSelect }) => {
  const selectable = entries.filter((e) => showPanelFiles || !PANEL_FILES[e.name]);
  const allSelected = selectable.length > 0 && selectable.every((e) => selectedSet.has(e.path));
  return (
    <label className="flex items-center" onClick={(e) => e.stopPropagation()}>
      <input
        type="checkbox"
        className="w-4 h-4 appearance-none border-2 border-border rounded cursor-pointer checked:bg-accent checked:border-accent relative
          after:content-[''] after:absolute after:left-[3px] after:top-[0.5px] after:w-[6px] after:h-[10px]
          after:border-white after:border-r-2 after:border-b-2 after:rotate-45 after:opacity-0 checked:after:opacity-100"
        checked={allSelected}
        onChange={() => {
          if (allSelected) {
            selectable.forEach((e) => onToggleSelect(e, false));
          } else {
            selectable.forEach((e) => onToggleSelect(e, true));
          }
        }} />
    </label>
  );
};

const STATUS_LABEL = { pending: 'Pending', 'in-progress': 'Moving', completed: 'Done', error: 'Error' };
const STATUS_CLASS = {
  pending: 'bg-zinc-500/20 text-zinc-300',
  'in-progress': 'bg-accent/20 text-accent',
  completed: 'bg-green-500/20 text-green-300',
  error: 'bg-red-500/20 text-red-200'
};

const FileList = ({
  entries,
  onOpen,
  onSelect,
  selected,
  selectedSet = new Set(),
  onToggleSelect,
  side,
  enableDrop,
  onDropFiles,
  containerStyle,
  onUp,
  onContextMenu,
  onContextMenuBlank,
  enableEntryDrag = false,
  dragFrom,
  onDropEntries,
  currentPath,
  showCheckboxes = false,
  openOnClick = false,
  statusMap,
  loadingPath,
  loadingFilePath,
  loadingUp = false,
  isRoot = false,
  activeTransferCount = 0,
  onMoveEntries,
  deletingPaths,
  showPanelFiles = false,
  onPanelFileWarning
}) => {
  const [dragOverTarget, setDragOverTarget] = useState(null);
  const [dragOverUp, setDragOverUp] = useState(false);

  const handleEntryDragOver = (e, entry) => {
    if (entry.type !== 'dir') return;
    e.preventDefault();
    e.stopPropagation();
    setDragOverTarget(entry.path);
  };

  const handleEntryDragLeave = () => {
    setDragOverTarget(null);
  };

  const handleEntryDrop = (e, targetEntry) => {
    setDragOverTarget(null);
    if (targetEntry.type !== 'dir') return;
    if (!onMoveEntries) return;
    e.preventDefault();
    e.stopPropagation();
    const data = e.dataTransfer.getData('application/x-sftp-entries');
    if (data) {
      try {
        const payload = JSON.parse(data);
        onMoveEntries(payload.paths, targetEntry.path);
      } catch (err) { /* ignore */ }
    }
  };

  const handleUpDragOver = (e) => {
    if (isRoot) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOverUp(true);
  };

  const handleUpDragLeave = () => {
    setDragOverUp(false);
  };

  const handleUpDrop = (e) => {
    setDragOverUp(false);
    if (isRoot || !onMoveEntries) return;
    e.preventDefault();
    e.stopPropagation();
    const data = e.dataTransfer.getData('application/x-sftp-entries');
    if (data) {
      try {
        const payload = JSON.parse(data);
        onMoveEntries(payload.paths, '..');
      } catch (err) { /* ignore */ }
    }
  };

  return (
    <div
      className="mt-3 flex flex-col overflow-hidden rounded-xl border border-border/80 bg-inset/60 min-h-0 relative"
      style={containerStyle}
      onDragOver={(e) => {
        if (enableDrop || onDropEntries) e.preventDefault();
      }}
      onDrop={(e) => {
        if (onDropEntries) {
          e.preventDefault();
          const data = e.dataTransfer.getData('application/x-sftp-entries');
          if (data) {
            try {
              const payload = JSON.parse(data);
              onDropEntries(payload);
              return;
            } catch (err) { /* ignore */ }
          }
        }
        if (enableDrop && onDropFiles) {
          e.preventDefault();
          const files = Array.from(e.dataTransfer.files || []);
          onDropFiles(files, e.dataTransfer);
        }
      }}>

      {activeTransferCount > 0 &&
        <div className="absolute inset-0 z-10 bg-black/50 backdrop-blur-[2px] flex flex-col items-center justify-center rounded-xl gap-3 pointer-events-none">
          <RiLoader4Line className="text-accent text-3xl animate-spin" />
          <span className="text-sm font-semibold text-white/80">Uploading...</span>
        </div>
      }

      <div className="grid grid-cols-[1.8fr,0.9fr] sm:grid-cols-[1.8fr,0.7fr,0.9fr] items-center border-b border-border/70 px-4 py-2.5 text-sm font-semibold text-textMuted">
        <span className="flex items-center gap-2">
          {showCheckboxes && onToggleSelect &&
            <SelectAllCheckbox
              entries={entries}
              selectedSet={selectedSet}
              showPanelFiles={showPanelFiles}
              onToggleSelect={onToggleSelect} />
          }
          {side === 'remote' ? <RiUpload2Line className="text-accent text-base" /> : <RiFileLine className="text-textMuted text-base" />}
          Name
        </span>
        <span className="hidden sm:inline text-right">Size</span>
        <span className="text-right">Modified</span>
      </div>
      <div
        className="flex-1 overflow-y-auto"
        onContextMenu={(e) => {
          if (!onContextMenuBlank) return;
          if (e.target.closest('button')) return;
          e.preventDefault();
          onContextMenuBlank(e);
        }}>

        {onUp ?
          <button
            type="button"
            onClick={isRoot ? undefined : onUp}
            disabled={isRoot}
            onDragOver={handleUpDragOver}
            onDragLeave={handleUpDragLeave}
            onDrop={handleUpDrop}
            className={`grid w-full grid-cols-[1.8fr,0.9fr] sm:grid-cols-[1.8fr,0.7fr,0.9fr] items-center px-4 py-2.5 text-base transition ${
              isRoot ? 'text-textMuted/30 cursor-default' :
              dragOverUp ? 'bg-accent/20 text-accent' :
              'text-textPrimary hover:bg-white/5'
            }`}>
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="shrink-0">
                {loadingUp ?
                  <RiLoader4Line className="text-accent text-lg animate-spin" /> :
                  <span className="text-xl leading-none">...</span>
                }
              </span>
              <span className="truncate">Up</span>
            </div>
            <span className="text-right text-sm text-textMuted" />
            <span className="text-right text-sm text-textMuted" />
          </button> :
          null}

        {entries.map((entry) => {
          const panelInfo = PANEL_FILES[entry.name];
          if (panelInfo && !showPanelFiles) return null;

          const isLoadingDir = loadingPath && entry.type === 'dir' && entry.path === loadingPath;
          const isLoadingFile = loadingFilePath && entry.type === 'file' && entry.path === loadingFilePath;
          const isDeleting = deletingPaths && deletingPaths.has(entry.path);
          const isLoading = isLoadingDir || isLoadingFile || isDeleting;
          const isDragTarget = dragOverTarget === entry.path;

          const handleClick = (e) => {
            if (e.target.closest('label') || e.target.tagName === 'INPUT') return;
            if (panelInfo && onPanelFileWarning) {
              onPanelFileWarning(entry, panelInfo);
              return;
            }
            if (showCheckboxes) {
              if (onToggleSelect) onToggleSelect(entry, !selectedSet.has(entry.path));
              return;
            }
            onSelect(entry, { ctrlKey: e.ctrlKey || e.metaKey });
            onOpen(entry);
          };

          let entryIcon;
          if (isLoading) {
            entryIcon = <RiLoader4Line className="text-accent text-lg animate-spin" />;
          } else if (panelInfo) {
            const PIcon = panelInfo.Icon;
            entryIcon = <PIcon className={`${panelInfo.color} text-lg`} />;
          } else if (entry.type === 'dir') {
            entryIcon = <RiFolderFill className="text-accent text-lg" />;
          } else if (entry.name?.toLowerCase().endsWith('.zip')) {
            entryIcon = <RiArchive2Fill className="text-accent text-lg" />;
          } else {
            entryIcon = <RiFileLine className="text-textMuted text-lg" />;
          }

          return (
            <button
              key={`${side}-${entry.path}`}
              type="button"
              onClick={handleClick}
              draggable={enableEntryDrag}
              onDragStart={(e) => {
                if (!enableEntryDrag) return;
                const paths =
                  selectedSet && selectedSet.size > 0 && selectedSet.has(entry.path) ?
                  Array.from(selectedSet) :
                  [entry.path];
                const payload = { from: dragFrom || side, paths, currentPath };
                e.dataTransfer.setData('application/x-sftp-entries', JSON.stringify(payload));
                e.dataTransfer.effectAllowed = 'copyMove';
              }}
              onDragOver={(e) => handleEntryDragOver(e, entry)}
              onDragLeave={handleEntryDragLeave}
              onDrop={(e) => handleEntryDrop(e, entry)}
              onContextMenu={(e) => {
                e.preventDefault();
                if (onContextMenu) onContextMenu(e, entry);
              }}
              className={`grid w-full grid-cols-[1.8fr,0.9fr] sm:grid-cols-[1.8fr,0.7fr,0.9fr] items-center px-4 py-2.5 text-base transition ${
                panelInfo ? 'opacity-60 hover:opacity-80' :
                isDragTarget ? 'bg-accent/20 text-accent' :
                selected?.path === entry.path || selectedSet.has(entry.path) ?
                'bg-accent/15 text-accent' :
                'hover:bg-white/5 text-textPrimary'}`
              }>

              <div className="flex items-center gap-2.5 min-w-0">
                {showCheckboxes && !panelInfo ?
                  <label className="flex shrink-0 items-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="w-4 h-4 appearance-none border-2 border-border rounded cursor-pointer checked:bg-accent checked:border-accent relative
                        after:content-[''] after:absolute after:left-[3px] after:top-[0.5px] after:w-[6px] after:h-[10px]
                        after:border-white after:border-r-2 after:border-b-2 after:rotate-45 after:opacity-0 checked:after:opacity-100"
                      checked={selectedSet.has(entry.path)}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (onToggleSelect) onToggleSelect(entry, e.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()} />
                  </label> :
                  null}
                <span className="shrink-0">{entryIcon}</span>
                <span className="truncate overflow-hidden whitespace-nowrap">
                  {panelInfo ? panelInfo.label : entry.name}
                </span>
                {panelInfo &&
                  <span className="shrink-0 rounded-full px-2 py-0.5 text-xs leading-tight bg-zinc-500/20 text-zinc-400">System</span>
                }
                {statusMap?.[entry.path] &&
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs leading-tight ${STATUS_CLASS[statusMap[entry.path]] || ''}`}>
                    {STATUS_LABEL[statusMap[entry.path]] || statusMap[entry.path]}
                  </span>
                }
              </div>
              <span className="hidden sm:inline text-right text-sm text-textMuted">{statusMap?.[entry.path] ? '' : formatSize(entry.size)}</span>
              <span className="text-right text-sm text-textMuted flex flex-col items-end leading-tight">
                {statusMap?.[entry.path] ? '' : (() => {
                  if (!entry.modified) return '--';
                  const d = new Date(entry.modified);
                  return <><span>{d.toLocaleDateString()}</span><span>{d.toLocaleTimeString()}</span></>;
                })()}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};


export default FileList;
